import React, { useEffect, useState } from "react";
import AdvisorMainContent from "../layout/AdvisorMain";
import AdvisorCmtsAndRecmndtns from "../components/Advisors/AdvisorCmtsAndRecmndtns";
import leftarrow from "../assets/images/u_angle-left-b.svg";
import { useLocation, useNavigate } from "react-router-dom";
import { Tooltip } from "antd";
import AdvisorReviewModal from "./AdvisorReviewModal";
import { apiServices } from "../services/Api";
import { safeJSONParse } from "../utils/jsonHelper";
import { Edit2 } from "lucide-react";

const Edittrade = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [data, setData] = useState(location.state);

  const callId = data?.callId || data?.id;

  const [logic, setLogic] = useState([]);
  const [recommendation, setRecommendation] = useState([]);
  const [traders, setTraders] = useState(0);

  useEffect(() => {
    const fetchTradersCount = async () => {
      try {
        if (callId) {
          const response = await apiServices.get(
            `advisors/calls/${callId}/tradersCount`
          );
          setTraders(response?.tradersCount ?? 0);
        }
      } catch (error) {
        console.error("Error fetching traders count:", error);
      }
    };
    fetchTradersCount();
  }, [callId]);

  useEffect(() => {
    if (!callId) return;
    const initialLogic = safeJSONParse(data?.comments, []);
    const initialRecommendations = safeJSONParse(data?.recommendations, []);
    setLogic(initialLogic);
    setRecommendation(initialRecommendations);
  }, [callId, data?.comments, data?.recommendations]);

  const [riskRewardRatio, setRiskRewardRatio] = useState("");

  function calculateRatio(entryL, stoploss, target) {
    if (
      !Number.isFinite(entryL) ||
      !Number.isFinite(stoploss) ||
      !Number.isFinite(target)
    ) {
      return "";
    }

    const denom = target - entryL;
    if (denom === 0) return "";

    return ((entryL - stoploss) / denom).toFixed(2);
  }

  function formatRatio(ratio) {
    if (ratio) {
      const fraction = parseFloat(ratio);
      const precision = 1e9;
      let numerator = Math.round(fraction * precision);
      let denominator = precision;
      const gcd = (a, b) => (b ? gcd(b, a % b) : a);
      const gcdValue = gcd(numerator, denominator);
      numerator /= gcdValue;
      denominator /= gcdValue;
      return { risk: numerator, reward: denominator };
    }
  }

  useEffect(() => {
    const ratio = calculateRatio(
      Number(data?.entryPriceLower),
      Number(data?.stoploss),
      Number(data?.targetPrice)
    );
    if (ratio) {
      const formattedRatio = formatRatio(ratio);
      if (formattedRatio?.risk && formattedRatio?.reward) {
        setRiskRewardRatio(
          `1 : ${(formattedRatio.reward / formattedRatio.risk).toFixed(2)}`
        );
      } else {
        setRiskRewardRatio("");
      }
    } else {
      setRiskRewardRatio("");
    }
  }, [data?.entryPriceLower, data?.stoploss, data?.targetPrice]);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const handlePublishCall = async () => {
    if (!callId) {
      alert("Error: Call ID is missing. Cannot update call.");
      return;
    }

    if (!data.tradingSymbol || !data.transactionType) {
      alert("Error: Missing required trading information.");
      return;
    }

    try {
      const payload = {
        id: callId,
        tradingSymbol: data.tradingSymbol,
        transactionType: data.transactionType,
        entryPriceLower: parseFloat(data.entryPriceLower) || 0,
        entryPriceUpper: parseFloat(data.entryPriceUpper) || 0,
        targetPrice: parseFloat(data.targetPrice) || 0,
        stoploss: parseFloat(data.stoploss) || 0,
        tradeType: data.tradeType || "INTRADAY",
        duration: parseInt(data.duration || data.holdingDurationInDays) || 0,
        confidence: parseFloat(data.confidence) || 0,
        comments: JSON.stringify(logic),
        recommendations: JSON.stringify(recommendation),
        instrumentToken: data.instrumentToken || "",
      };

      const response = await apiServices.update(`advisors/calls`, payload);
      const success = response?.success ?? true;
      const message =
        response?.message ||
        (success ? "Call updated successfully" : "Failed to update call");

      if (success) {
        setIsModalOpen(false);
        alert(message);
        navigate(data?.from || "/advisordashboard", { state: data.userId });
      } else {
        alert(message);
      }
    } catch (err) {
      console.error("API Error:", err);
      const errorMessage =
        err?.message || "Failed to update call. Please try again.";
      alert(`Error: ${errorMessage}`);
    }
  };

  if (!data) return null;

  return (
    <AdvisorMainContent>
      <style>
        {`
          /* Hide number input spinners for Chrome, Safari, Edge, Opera */
          input[type="number"]::-webkit-outer-spin-button,
          input[type="number"]::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }

          /* Hide number input spinners for Firefox */
          input[type="number"] {
            -moz-appearance: textfield;
          }
        `}
      </style>
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        
        <div className="bg-white rounded-lg border border-brand-border p-4 lg:p-6 mb-6">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 w-full lg:w-auto">
              <div className="flex items-center gap-3 sm:gap-4">
                <button
                  onClick={() =>
                    navigate(data?.from || "/advisordashboard", { state: data.userId })
                  }
                  className="flex items-center justify-center w-10 h-10 lg:w-12 lg:h-12 rounded-lg border border-gray-400 bg-white hover:bg-gray-50 transition-colors"
                >
                  <img
                    src={leftarrow}
                    alt="back"
                    className="w-5 h-5 lg:w-6 lg:h-6"
                  />
                </button>
                <h1 className="text-lg lg:text-xl font-bold text-custom-darkGray">
                  Edit Trade Call
                </h1>
              </div>

              <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                <span>Traders</span>
                <span className="font-semibold text-custom-darkGray">
                  {traders}
                </span>
              </div>
            </div>
            <button
              onClick={openModal}
              className="w-full sm:w-auto px-6 lg:px-8 py-2.5 lg:py-3 bg-primary hover:bg-primary/80 text-white text-sm lg:text-base font-medium rounded-lg transition-colors"
            >
              Publish
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-brand-border p-4 lg:p-6 mb-6">
          <h2 className="text-base lg:text-lg font-semibold text-custom-darkGray mb-4 lg:mb-6">
            Trade call details
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 lg:gap-6 mb-6 pb-6 border-b border-brand-border">
            <div className="flex flex-col gap-2">
              <label className="text-xs lg:text-sm text-gray-600">
                Scrip Name
              </label>
              <div className="flex items-center gap-2 min-w-0">
                <Tooltip title={data.tradingSymbol}>
                  <span className="text-sm lg:text-base font-semibold text-custom-darkGray truncate max-w-[14rem] sm:max-w-[18rem]">
                    {data.tradingSymbol}
                  </span>
                </Tooltip>
                <span
                  className={`px-2 py-0.5 text-xs font-bold text-white rounded ${
                    data?.transactionType === "SELL"
                      ? "bg-brand-red"
                      : "bg-primary-green"
                  }`}
                >
                  {data.transactionType}
                </span>
              </div>
            </div>

            {/* Current Price */}
            <div className="flex flex-col gap-2">
              <label className="text-xs lg:text-sm text-gray-600">
                Current Price
              </label>
              <span className="text-sm lg:text-base font-semibold text-custom-darkGray">
                Rs {data.price}
              </span>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-xs lg:text-sm text-gray-600">Status</label>
              <span className="text-sm lg:text-base font-semibold text-custom-darkGray">
                {data.status}
              </span>
            </div>

         
            <div className="flex flex-col gap-2">
              <label className="text-xs lg:text-sm text-gray-600">
                Confidence
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={data.confidence}
                  onChange={(e) =>
                    setData({ ...data, confidence: e.target.value })
                  }
                  className="w-full max-w-[110px] px-2 py-1 text-sm lg:text-base font-semibold text-custom-darkGray border-b-2 border-gray-300 focus:border-primary outline-none"
                />
                <span className="text-sm lg:text-base font-semibold">%</span>
                <button className="text-primary hover:text-primary/80">
                  <Edit2 size={16} />
                </button>
              </div>
            </div>

           
            <div className="flex flex-col gap-2">
              <label className="text-xs lg:text-sm text-gray-600">
                Entry Price Range
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="number"
                  value={data.entryPriceLower}
                  onChange={(e) =>
                    setData({ ...data, entryPriceLower: e.target.value })
                  }
                  className="w-full max-w-[120px] px-2 py-1 text-sm lg:text-base font-semibold text-custom-darkGray border-b-2 border-gray-300 focus:border-primary outline-none"
                />
                <span className="text-sm">to</span>
                <input
                  type="number"
                  value={data.entryPriceUpper}
                  onChange={(e) =>
                    setData({ ...data, entryPriceUpper: e.target.value })
                  }
                  className="w-full max-w-[120px] px-2 py-1 text-sm lg:text-base font-semibold text-custom-darkGray border-b-2 border-gray-300 focus:border-primary outline-none"
                />
                <button className="text-primary hover:text-primary/80">
                  <Edit2 size={16} />
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 lg:gap-6">
            <div className="flex flex-col gap-2">
              <label className="text-xs lg:text-sm text-gray-600">
                Duration
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={data.duration || data.holdingDurationInDays || ""}
                  onChange={(e) =>
                    setData({
                      ...data,
                      duration: e.target.value,
                      holdingDurationInDays: e.target.value,
                    })
                  }
                  className="w-full max-w-[110px] px-2 py-1 text-sm lg:text-base font-semibold text-custom-darkGray border-b-2 border-gray-300 focus:border-primary outline-none"
                />
                <span className="text-sm">Days</span>
                <button className="text-primary hover:text-primary/80">
                  <Edit2 size={16} />
                </button>
              </div>
            </div>

           
            <div className="flex flex-col gap-2">
              <label className="text-xs lg:text-sm text-gray-600">Target</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={data.targetPrice}
                  onChange={(e) =>
                    setData({ ...data, targetPrice: e.target.value })
                  }
                  className="w-full max-w-[140px] px-2 py-1 text-sm lg:text-base font-semibold text-custom-darkGray border-b-2 border-gray-300 focus:border-primary outline-none"
                />
                <button className="text-primary hover:text-primary/80">
                  <Edit2 size={16} />
                </button>
              </div>
            </div>

            
            <div className="flex flex-col gap-2">
              <label className="text-xs lg:text-sm text-gray-600">SL</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={data.stoploss}
                  onChange={(e) =>
                    setData({ ...data, stoploss: e.target.value })
                  }
                  className="w-full max-w-[140px] px-2 py-1 text-sm lg:text-base font-semibold text-custom-darkGray border-b-2 border-gray-300 focus:border-primary outline-none"
                />
                <button className="text-primary hover:text-primary/80">
                  <Edit2 size={16} />
                </button>
              </div>
            </div>

           
            <div className="flex flex-col gap-2">
              <label className="text-xs lg:text-sm text-gray-600">
                Risk : Reward
              </label>
              <span className="text-sm lg:text-base font-semibold text-custom-darkGray">
                {riskRewardRatio || "-"}
              </span>
            </div>

            {/* Funds Margin */}
            {/* <div className="flex flex-col gap-2">
              <label className="text-xs lg:text-sm text-gray-600">
                Funds Margin
              </label>
              <span className="text-sm lg:text-base font-semibold text-custom-darkGray">
                3 times
              </span>
            </div> */}
          </div>
        </div>

        {/* Add Comments */}
        <div className="bg-white rounded-lg border border-brand-border p-4 lg:p-6">
          <h2 className="text-base lg:text-lg font-semibold text-custom-darkGray mb-4 lg:mb-6">
            Add Comments
          </h2>
          <AdvisorCmtsAndRecmndtns
            logic={logic}
            setLogic={setLogic}
            recommendation={recommendation}
            setRecommendation={setRecommendation}
            updateLogicRecommendation={() => {}}
          />
        </div>

        {/* Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black bg-opacity-50"
              onClick={closeModal}
            ></div>

            {/* Modal Content */}
            <div className="relative bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto z-10">
              <AdvisorReviewModal
                data={{
                  ...data,
                  comments: logic,
                  recommendations: recommendation,
                }}
                showAdvDetails={true}
              />
              <div className="flex gap-3 lg:gap-4 p-4 lg:p-6 justify-end border-t border-gray-200 bg-white sticky bottom-0">
                <button
                  onClick={closeModal}
                  className="px-6 py-2.5 rounded-lg bg-white border border-primary text-custom-darkGray font-medium hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={handlePublishCall}
                  className="px-6 py-2.5 rounded-lg bg-primary text-white font-medium hover:bg-primary/80 transition-colors"
                >
                  Publish Call
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdvisorMainContent>
  );
};

export default Edittrade;
